import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { v2Styles as styles } from "./LocationSummaryV2.styles";
import { PAGE_SIZES } from "./LocationSummaryV2Table.shared";

interface Props {
  totalRows: number;
  pageSize: number;
  page: number;
  pages: number;
  onPageSizeChange: (size: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onFirst?: () => void;
  onLast?: () => void;
  /** True while an API call is in flight — disables all pager/page-size controls. */
  disabled?: boolean;
}

const LocationSummaryPagination: React.FC<Props> = ({
  totalRows,
  pageSize,
  page,
  pages,
  onPageSizeChange,
  onPrev,
  onNext,
  onFirst,
  onLast,
  disabled = false,
}) => {
  const { width, height } = useWindowDimensions();
  const isCompactMobile = Platform.OS !== "web" && width < 768;
  const [isSizeMenuOpen, setIsSizeMenuOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState<"below" | "above">("below");
  const [menuMaxHeight, setMenuMaxHeight] = useState(128);
  const pageSizeAnchorRef = useRef<View>(null);

  const atFirst = page === 0;
  const atLast = page >= pages - 1;
  const start = totalRows > 0 ? page * pageSize + 1 : 0;
  const end = totalRows > 0 ? Math.min((page + 1) * pageSize, totalRows) : 0;

  const rowCountLabel = useMemo(
    () => (totalRows > 0 ? `Showing ${start}–${end} of ${totalRows}` : "0 locations"),
    [end, start, totalRows]
  );

  const togglePageSizeMenu = useCallback(() => {
    if (isSizeMenuOpen) {
      setIsSizeMenuOpen(false);
      return;
    }

    const MENU_VERTICAL_GAP = 4;
    const MENU_DESIRED_HEIGHT = PAGE_SIZES.length * 36 + 8;

    pageSizeAnchorRef.current?.measureInWindow((_x, y, _w, anchorHeight) => {
      const spaceBelow = height - (y + anchorHeight) - 8;
      const spaceAbove = y - 8;

      const shouldOpenBelow =
        spaceBelow >= MENU_DESIRED_HEIGHT || spaceBelow >= spaceAbove;

      setMenuPlacement(shouldOpenBelow ? "below" : "above");
      setMenuMaxHeight(
        Math.max(
          72,
          Math.min(
            MENU_DESIRED_HEIGHT,
            shouldOpenBelow ? spaceBelow : spaceAbove
          )
        )
      );
      setIsSizeMenuOpen(true);
    });
  }, [height, isSizeMenuOpen]);

  if (isCompactMobile) {
    return (
      <View style={[styles.footer, mobileStyles.footerCompact]}>
        <Text style={[styles.footerText, mobileStyles.rowCountText]} numberOfLines={1}>
          {rowCountLabel}
        </Text>

        <View style={mobileStyles.mobilePager}>
          <View style={mobileStyles.mobileNavGroup}>
            <TouchableOpacity
              style={[styles.pagerNavBtn, (atFirst || disabled) && styles.pagerBtnDisabled]}
              disabled={atFirst || disabled}
              onPress={onPrev}
              testID="asn-v2-page-prev"
            >
              <MaterialIcons name="chevron-left" size={18} color={atFirst ? "#B0B7C3" : "#5E6673"} />
            </TouchableOpacity>

            <Text style={[styles.pagerPageLabel, mobileStyles.mobilePageLabel]} numberOfLines={1}>{`${page + 1} of ${pages}`}</Text>

            <TouchableOpacity
              style={[styles.pagerNavBtn, (atLast || disabled) && styles.pagerBtnDisabled]}
              disabled={atLast || disabled}
              onPress={onNext}
              testID="asn-v2-page-next"
            >
              <MaterialIcons name="chevron-right" size={18} color={atLast ? "#B0B7C3" : "#034895"} />
            </TouchableOpacity>
          </View>

          <View ref={pageSizeAnchorRef} style={mobileStyles.pageSizeDropdownWrap}>
            <TouchableOpacity
              style={[mobileStyles.pageSizeDropdownButton, disabled && styles.pagerBtnDisabled]}
              onPress={togglePageSizeMenu}
              disabled={disabled}
              testID="asn-v2-page-size-dropdown"
            >
              <Text style={mobileStyles.pageSizeDropdownText}>{pageSize}</Text>
              <MaterialIcons name="arrow-drop-down" size={16} color="#075CB5" />
            </TouchableOpacity>

            {isSizeMenuOpen && (
              <View
                style={[
                  mobileStyles.pageSizeMenu,
                  menuPlacement === "below"
                    ? mobileStyles.pageSizeMenuBelow
                    : mobileStyles.pageSizeMenuAbove,
                ]}
              >
                <ScrollView
                  style={[mobileStyles.pageSizeMenuScroll, { maxHeight: menuMaxHeight }]}
                  showsVerticalScrollIndicator
                  nestedScrollEnabled
                >
                  {PAGE_SIZES.map((size) => (
                    <TouchableOpacity
                      key={size}
                      style={[
                        mobileStyles.pageSizeMenuItem,
                        pageSize === size && mobileStyles.pageSizeMenuItemActive,
                      ]}
                      onPress={() => {
                        onPageSizeChange(size);
                        setIsSizeMenuOpen(false);
                      }}
                      testID={`asn-v2-page-size-${size}`}
                    >
                      <Text
                        style={[
                          mobileStyles.pageSizeMenuItemText,
                          pageSize === size && mobileStyles.pageSizeMenuItemTextActive,
                        ]}
                      >
                        {size}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.footer}>
      {/* Left — row count */}
      <Text style={styles.footerText}>
        {rowCountLabel}
      </Text>

      {/* Center — pager (absolutely positioned) */}
      <View style={styles.pagerCenter}>
        <View style={styles.pager}>
          {/* First page */}
          <TouchableOpacity
            style={[styles.pagerNavBtn, (atFirst || disabled) && styles.pagerBtnDisabled]}
            disabled={atFirst || disabled}
            onPress={onFirst}
            testID="asn-v2-page-first"
          >
            <MaterialIcons name="first-page" size={18} color={atFirst ? "#B0B7C3" : "#5E6673"} />
          </TouchableOpacity>
          {/* Prev */}
          <TouchableOpacity
            style={[styles.pagerNavBtn, (atFirst || disabled) && styles.pagerBtnDisabled]}
            disabled={atFirst || disabled}
            onPress={onPrev}
            testID="asn-v2-page-prev"
          >
            <MaterialIcons name="chevron-left" size={18} color={atFirst ? "#B0B7C3" : "#5E6673"} />
          </TouchableOpacity>
          {/* Page label with boxed number */}
          <Text style={styles.pagerPageLabel}>Page</Text>
          <View style={styles.pagerPageBox}>
            <Text style={styles.pagerPageBoxText}>{page + 1}</Text>
          </View>
          <Text style={styles.pagerPageLabel}>{`of ${pages}`}</Text>
          {/* Next */}
          <TouchableOpacity
            style={[styles.pagerNavBtn, (atLast || disabled) && styles.pagerBtnDisabled]}
            disabled={atLast || disabled}
            onPress={onNext}
            testID="asn-v2-page-next"
          >
            <MaterialIcons name="chevron-right" size={18} color={atLast ? "#B0B7C3" : "#034895"} />
          </TouchableOpacity>
          {/* Last page */}
          <TouchableOpacity
            style={[styles.pagerNavBtn, (atLast || disabled) && styles.pagerBtnDisabled]}
            disabled={atLast || disabled}
            onPress={onLast}
            testID="asn-v2-page-last"
          >
            <MaterialIcons name="last-page" size={18} color={atLast ? "#B0B7C3" : "#034895"} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Right — page size buttons */}
      <View style={styles.pageSizeRow}>
        {PAGE_SIZES.map((size) => (
          <TouchableOpacity
            key={size}
            style={[
              styles.pageButton,
              pageSize === size && styles.pageButtonActive,
              disabled && styles.pagerBtnDisabled,
            ]}
            onPress={() => onPageSizeChange(size)}
            disabled={disabled}
            testID={`asn-v2-page-size-${size}`}
          >
            <Text style={[styles.pageText, pageSize === size && styles.pageTextActive]}>
              {size}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const mobileStyles = {
  footerCompact: {
    flexWrap: "nowrap" as const,
    justifyContent: "space-between" as const,
    gap: 6,
    paddingVertical: 10,
    overflow: "visible" as const,
  },
  rowCountText: {
    flex: 1,
    minWidth: 0,
    marginRight: 6,
  },
  mobilePager: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 4,
    overflow: "visible" as const,
  },
  mobileNavGroup: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
  },
  mobilePageLabel: {
    minWidth: 44,
    textAlign: "center" as const,
  },
  pageSizeDropdownWrap: {
    position: "relative" as const,
    marginLeft: 4,
    overflow: "visible" as const,
  },
  pageSizeDropdownButton: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    minWidth: 54,
    height: 28,
    borderWidth: 1,
    borderColor: "#CAD3DF",
    borderRadius: 5,
    paddingHorizontal: 8,
    backgroundColor: "#FFFFFF",
  },
  pageSizeDropdownText: {
    color: "#075CB5",
    fontSize: 12,
    fontWeight: "700" as const,
  },
  pageSizeMenu: {
    position: "absolute" as const,
    right: 0,
    width: 120,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DCE3EC",
    paddingVertical: 4,
    zIndex: 1000,
    shadowColor: "#3A3A3A",
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 10,
  },
  pageSizeMenuBelow: {
    top: 32,
  },
  pageSizeMenuAbove: {
    bottom: 32,
  },
  pageSizeMenuScroll: {
    minHeight: 72,
  },
  pageSizeMenuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  pageSizeMenuItemActive: {
    backgroundColor: "#EFF5FC",
  },
  pageSizeMenuItemText: {
    fontSize: 12,
    color: "#075CB5",
    fontWeight: "600" as const,
    textAlign: "center" as const,
  },
  pageSizeMenuItemTextActive: {
    fontWeight: "700" as const,
  },
};

export default LocationSummaryPagination;
